import React from "react";
import { motion } from "framer-motion";
import { Radar, Crown, Lock, Activity, ScanLine, ArrowRight, Sparkles, Flame, Trophy } from "lucide-react";
import SkeletalHudMock from "./SkeletalHudMock";
import { ElitePlayerCard } from "../engine/ElitePlayerCard";
import { ProRadar } from "../engine/ProScoutCharts";
import { TickerStrip } from "../elite/TechnicalTicker";

const GOLD = "#D4AF37", RED = "#C41E24";
export const DEMO_PLAYER = { name: "Marcus Reed", display_name: "Marcus Reed", tier: "elite", club_name: "Central IL FC", school_name: "Normal West HS", jersey_number: 10 };
export const DEMO_ENGINE = { balance: 9.2, execution: 9.4, pace: 9.0, elite: true };
export const DEMO_TICKER = [
  { icon: Sparkles, k: "Personal Best", v: "9.2 / 10", s: "Elastico", c: GOLD },
  { icon: Flame, k: "Training Streak", v: "21 days", s: "longest 34", c: RED },
  { icon: Activity, k: "Total Reps", v: "12,480", s: "94 sessions", c: GOLD },
  { icon: Trophy, k: "Leaderboard", v: "#1", s: "2,450 pts", c: RED },
  { icon: Crown, k: "Achievement", v: "Elite Mastered · Elastico", s: "", c: GOLD },
];

const fade = (d = 0) => ({ initial: { opacity: 0, y: 26 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.6, delay: d, ease: "easeOut" } });

const Kicker = ({ icon: Icon, children, color = GOLD }) => (
  <div className="inline-flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.35em] mb-4" style={{ color }}><Icon size={13} /> {children}</div>
);

const LabPanel = ({ title, body, children, kicker, icon, testid, delay, reverse }) => (
  <motion.div {...fade(delay)} data-testid={testid} className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
    <div>
      <Kicker icon={icon}>{kicker}</Kicker>
      <h3 className="font-cover text-4xl sm:text-5xl uppercase tracking-tight leading-[0.9] mb-4">{title}</h3>
      <p className="text-white/65 text-base leading-relaxed max-w-lg">{body}</p>
    </div>
    <div className="relative min-w-0">
      <div className="absolute -inset-8 rounded-[3rem] pointer-events-none" style={{ background: `radial-gradient(circle at 50% 50%, ${GOLD}2e, transparent 68%)` }} />
      <div className="relative min-w-0">{children}</div>
    </div>
  </motion.div>
);

// Landing: "Inside the Lab" — the Diamond product visuals rendered from the live Elite components.
export default function LabShowcase({ onCta, ctaLabel, disabled }) {
  return (
    <section data-testid="lab-showcase-section" className="relative py-24 sm:py-32 px-6 sm:px-10 elite-carbon-bg border-y border-[#D4AF37]/20 overflow-hidden">
      <div aria-hidden className="absolute inset-0 elite-mesh-overlay pointer-events-none" />
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(900px 520px at 90% 0%, ${GOLD}1f, transparent 60%), radial-gradient(700px 480px at 0% 100%, ${RED}22, transparent 60%)` }} />
      <div className="relative max-w-6xl mx-auto">
        <motion.div {...fade()} className="max-w-3xl mb-20">
          <Kicker icon={ScanLine}>Inside the Lab · Next O Technical Performance Engine</Kicker>
          <h2 className="font-cover text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tight leading-[0.88] mb-5">
            Sports-Lab Tech.<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F5E7B8] via-[#D4AF37] to-[#B8912E]">Backyard Reps.</span>
          </h2>
          <p className="text-white/65 text-lg leading-relaxed">Upload a 15-second clip. The Engine freezes the critical frame, maps the skeleton, scores Balance · Execution · Game Pace, and mints your player's Elite Card. Every rep becomes data. Every week becomes proof.</p>
        </motion.div>

        {/* Widescreen lab tour — desktop only (mobile gets the 9:16 reel in Sneak Peek) */}
        <motion.div {...fade(0.1)} className="hidden md:block mb-24" data-testid="lab-wide-tour">
          <div className="relative rounded-3xl overflow-hidden border border-[#D4AF37]/30 bg-black" style={{ boxShadow: `0 0 60px ${GOLD}22, 0 40px 100px rgba(0,0,0,0.7)` }}>
            <video data-testid="lab-wide-video" src="/marketing/app-tour-2026-wide.mp4" poster="/marketing/app-tour-2026-wide-poster.jpg" controls muted autoPlay loop playsInline preload="metadata" className="w-full block aspect-video" />
            <div className="absolute top-4 left-4 flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] bg-black/60 px-3 py-1.5 rounded-sm pointer-events-none"><span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" /> 60-second lab tour · turn on sound</div>
          </div>
        </motion.div>

        <div className="space-y-24 sm:space-y-32">
          <LabPanel testid="lab-hud" kicker="Freeze-Frame · Skeletal HUD" icon={ScanLine} title={<>Biomechanics,<br />Not Opinions.</>}
            body="Mission Control locks onto the plant foot, hips and knee at the exact moment the move breaks down — joint angles, stability index and hip drive read out live, with a slow-motion fix clip and Coach Barker's voice brief.">
            <div className="max-w-[380px] mx-auto"><SkeletalHudMock /></div>
          </LabPanel>

          <LabPanel testid="lab-card" reverse kicker="Animated Elite Card" icon={Crown} title={<>Your Rating.<br />Minted Every Scan.</>}
            body="Every Elite scan updates a FIFA-style Technical Card — live Balance, Execution and Game-Pace ratings, club & school crests, and the gold glint only Tier 3 players carry. Share it. Chase it. Earn the 90.">
            <div className="rounded-3xl border border-[#D4AF37]/25 bg-[#0b0b0b]/80 backdrop-blur-xl p-5 sm:p-7 elite-card-halo">
              <ElitePlayerCard player={DEMO_PLAYER} engine={DEMO_ENGINE} drill="Elastico · Scan 07" api="" animated />
            </div>
          </LabPanel>

          <LabPanel testid="lab-radar" kicker="Pro-Scout Radar · Trend Lines" icon={Radar} title={<>Watch the<br />Transformation.</>}
            body="Neon radar charts and trend lines track every axis against the Next O Standard of 9.0. Parents see the gap close week over week — the most convincing progress report a player can hand you.">
            <div className="space-y-4">
              <ProRadar engine={DEMO_ENGINE} />
              <div className="rounded-xl border border-[#D4AF37]/40 overflow-hidden bg-[#070707]" style={{ boxShadow: `0 0 22px ${GOLD}33` }}>
                <div className="flex items-stretch h-10">
                  <div className="flex items-center gap-1.5 px-3 shrink-0 border-r border-[#D4AF37]/30 bg-[#D4AF37]/10"><span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" /><span className="font-mono text-[9px] font-bold tracking-[0.3em] uppercase text-[#D4AF37]">Live</span></div>
                  <TickerStrip items={DEMO_TICKER} className="flex-1" speed="28s" />
                </div>
              </div>
            </div>
          </LabPanel>
        </div>

        {/* Diamond gallery */}
        <motion.div {...fade()} className="mt-28">
          <Kicker icon={Lock}>The Technical Academy · Elite Tier</Kicker>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="diamond-gallery">
            {[
              { src: "/marketing/diamond-card.jpg", t: "Animated Elite Card" },
              { src: "/marketing/diamond-feed.jpg", t: "Private Locker Room" },
              { src: "/marketing/diamond-academy.jpg", t: "Live Technical Ticker" },
              { src: "/marketing/diamond-transformation.jpg", t: "Before & After Radar" },
            ].map((g, i) => (
              <motion.figure key={g.src} {...fade(i * 0.08)} data-testid={`diamond-shot-${i}`} className="group relative rounded-2xl overflow-hidden border border-[#D4AF37]/25 bg-black" style={{ boxShadow: `0 20px 60px rgba(0,0,0,0.6)` }}>
                <img src={g.src} alt={g.t} loading="lazy" className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.04]" style={{ aspectRatio: "4 / 5", objectFit: "cover" }} />
                <figcaption className="absolute inset-x-0 bottom-0 px-3 py-2.5 bg-gradient-to-t from-black/90 to-transparent font-mono text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">{g.t}</figcaption>
              </motion.figure>
            ))}
          </div>
          <div className="mt-12 flex flex-col sm:flex-row items-center gap-4 justify-center">
            <button data-testid="lab-cta" onClick={onCta} disabled={disabled}
              className="cta-glow group inline-flex items-center gap-2 bg-gradient-to-r from-[#E7C64B] to-[#C39A2B] hover:brightness-110 text-black font-subheadings font-bold uppercase tracking-wider px-8 py-4 rounded-full transition-all disabled:opacity-70">
              <Crown size={16} /> {ctaLabel} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <span className="text-white/45 text-sm">Start with the Blueprint · enroll in Elite anytime</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
